import axios from 'axios';
import { API_URL } from '../config';
import moment from 'moment';

const BASE = `${API_URL}/firme/api`;

export type Firmatario = {
  cf: string;
  surname: string;
  name: string;
  email: string;
  cellNumber: string;
  descrizione?: string;
  fontSize?: 'small' | 'medium' | 'large';
  signPosition: { pag: number; x: number; y: number };
  // Valorizzati dal backend dopo la creazione
  signUrl?: string;
  providerSignerId?: string;
};

export type SignedDocEmployee = {
  id: number;
  first_name?: string;
  last_name?: string;
  cf?: string;
};

export type SignedDoc = {
  id: number;
  name: string;
  description?: string;
  employee: SignedDocEmployee | null;
  file_name?: string;
  file_size?: number;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  is_signed: boolean;
  has_signed_file: boolean;
  visible_to_employee: boolean;
  provider_id: string | null;
  provider_name: string;
  signature_service?: string;
  read_at: string | null;
  status?: 'UPLOAD' | 'READYTOSIGN' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'ERROR';
  provider_error_code?: number | null;
  provider_error_message?: string;
};

export type SignedDocDetail = SignedDoc & {
  original_file_url: string | null;
  signed_file_url: string | null;
  firmatari: Firmatario[];
};

export type CompanySignStats = {
  azienda: { id: number; ragione_sociale: string; piva: string };
  statistiche_totali: { total: number; firmati: number; da_firmare: number };
  statistiche_dipendenti: {
    id: number;
    nome: string;
    cognome: string;
    cf: string;
    total_documenti: number;
    documenti_firmati: number;
    documenti_da_firmare: number;
  }[];
};

export const isSentToProvider = (doc: Pick<SignedDoc, 'provider_id'>): boolean => !!doc.provider_id;

export type SignedDocStatus = 'signed' | 'not_sent' | 'pending' | 'error' | 'rejected';

export const signedDocStatus = (doc: SignedDoc): SignedDocStatus => {
  if (doc.is_signed) return 'signed';
  if (doc.status === 'ERROR') return 'error';
  if (doc.status === 'REJECTED') return 'rejected';
  if (!isSentToProvider(doc)) return 'not_sent';
  return 'pending';
};

export const isSignedDocExpired = (
  doc: SignedDoc,
  now: Date = new Date()
): boolean => {
  if (!doc.due_date) return false;

  const dueDate = moment(doc.due_date);

  if (!dueDate.isValid()) return false;

  return dueDate.isBefore(moment(now), 'day');
};

export const isAwaitingEmployeeSignature = (doc: SignedDoc): boolean =>
  !doc.is_signed && isSentToProvider(doc);

export const countAwaitingEmployeeSignature = (docs: SignedDoc[]): number =>
  docs.filter(isAwaitingEmployeeSignature).length;

export const canManageSignedDocs = (profile: any): boolean =>
  profile?.customer_type !== 'employee' ||
  !!(
    profile?.employee?.permissions?.includes('hr.view_docs_ou') ||
    profile?.employee?.permissions?.includes('hr.view_docs_all')
  );

// Stessa regola di CheckPermissions sulla rotta /areariservata/firme
export const canViewOwnSignedDocs = (profile: any): boolean =>
  profile?.customer_type === 'consultant' ||
  profile?.customer_type === 'company' ||
  !!profile?.employee?.permissions?.includes('hr.view_docs');

const cfg = (token: string, signal: AbortSignal | null = null, extra: any = {}) => {
  const config: any = { headers: { Authorization: `${token}` }, ...extra };
  if (signal) config.signal = signal;
  return config;
};

// L'endpoint può rispondere con un array o con un oggetto paginato
const asList = (data: any): SignedDoc[] =>
  Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];

export const getSignedDocs = async (
  token: string,
  {
    employeeId,
    isSigned,
    visibleOnly,
  }: { employeeId?: number; isSigned?: boolean; visibleOnly?: boolean } = {},
  signal: AbortSignal | null = null
): Promise<SignedDoc[]> => {
  const params: any = {};
  if (employeeId) params.employee_id = employeeId;
  if (isSigned !== undefined) params.is_signed = String(isSigned);
  if (visibleOnly !== undefined) params.visible_only = String(visibleOnly);
  const res = await axios.get(`${BASE}/documents/`, cfg(token, signal, { params }));
  return asList(res.data);
};

export const getSignedDoc = async (
  token: string,
  id: number,
  signal: AbortSignal | null = null
): Promise<SignedDocDetail> => {
  const res = await axios.get(`${BASE}/documents/${id}/`, cfg(token, signal));
  return res.data;
};

export const createSignedDoc = async (
  token: string,
  data: {
    name: string;
    description?: string;
    employeeIds: number[];
    file: File;
    firmatari: Firmatario[];
    visibleToEmployee?: boolean;
    dueDate?: string; // 'YYYY-MM-DD'
  }
): Promise<SignedDoc> => {
  const fd = new FormData();
  fd.append('name', data.name);

  if (data.description) {
    fd.append('description', data.description);
  }

  // Elenco degli ID dei dipendenti associati al documento
  fd.append('employee_ids', JSON.stringify(data.employeeIds));

  fd.append('original_file', data.file);
  fd.append('firmatari', JSON.stringify(data.firmatari));

  if (data.dueDate) {
    fd.append('due_date', data.dueDate);
  }

  // Di default visible_to_employee è true
  if (data.visibleToEmployee === false) {
    fd.append('visible_to_employee', 'False');
  }

  const res = await axios.post(
    `${BASE}/documents/`,
    fd,
    cfg(token)
  );
  return res.data;
};

// Segna il documento come letto dal dipendente
export const markSignedDocRead = async (token: string, id: number) => {
  const res = await axios.patch(
    `${BASE}/documents/${id}/`,
    { read_at: new Date().toISOString() },
    cfg(token)
  );
  return res.data;
};

// Prossimi firmatari secondo FirmoSemplice (lista di stringhe). null = informazione non disponibile:
// una risposta in formato inatteso non va letta come "nessuno deve più firmare"
export const getNextSigners = async (
  token: string,
  id: number,
  signal: AbortSignal | null = null
): Promise<string[] | null> => {
  const res = await axios.get(`${BASE}/documents/${id}/next-signers/`, cfg(token, signal));
  const list = res.data?.next_signers;
  return Array.isArray(list) ? list.map(String) : null;
};

export const deleteSignedDoc = async (token: string, id: number) => {
  await axios.delete(`${BASE}/documents/${id}/`, cfg(token));
};

export const firmoDeleteSignedDoc = async (token: string, id: number) => {
  const res = await axios.delete(`${BASE}/documents/${id}/firmo-delete/`, cfg(token));
  return res.data;
};

// Reinvia le email di invito ai firmatari (e sincronizza lo stato)
export const resendSignedDocInvites = async (token: string, id: number) => {
  const res = await axios.post(`${BASE}/documents/${id}/resend/`, {}, cfg(token));
  return res.data;
};

// Sincronizza lo stato dal provider (scarica il PDF firmato se completato)
export const refreshSignedDocStatus = async (token: string, id: number) => {
  const res = await axios.get(`${BASE}/documents/${id}/status/`, cfg(token));
  return res.data;
};

// Specchio di firme/providers/phone.py (normalize_mobile): E.164 oppure null
export const normalizeMobile = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  let s = String(value).replace(/[\s\-.()]/g, '');
  if (!s) return null;
  if (s.startsWith('00')) s = '+' + s.slice(2);
  if (!s.startsWith('+') && /^[0-9]+$/.test(s) && s.length >= 9 && s.length <= 11) s = '+39' + s;
  return /^\+[1-9][0-9]{6,14}$/.test(s) ? s : null;
};

// Misure del box firma lato provider e shift applicati dal backend:
// specchio di firme/providers/openapi_esign.py (_convert_position)
export const PROVIDER_BOX_W = 100;
export const PROVIDER_BOX_H = 42;
export const FS_X_SHIFT = 28;
export const FS_Y_SHIFT = 35;

// (x, y) = CENTRO del marker in punti PDF con origine in alto a sinistra, pageH = altezza pagina in punti.
// Restituisce la convenzione FirmoSemplice (origine in basso a sinistra) attesa dal backend.
// Unico punto in cui avviene la conversione.
export const toProviderSignPosition = ({
  pag,
  x,
  y,
  pageH,
}: {
  pag: number;
  x: number;
  y: number;
  pageH: number;
}): { pag: number; x: number; y: number } => {
  const xFs = Math.round(x - PROVIDER_BOX_W / 2 + FS_X_SHIFT);
  const yFs = Math.round(pageH - (y - PROVIDER_BOX_H / 2) - PROVIDER_BOX_H - FS_Y_SHIFT);
  return { pag, x: Math.max(0, xFs), y: Math.max(0, yFs) };
};

export const downloadPartialSignedDoc = async (token: string, id: number): Promise<Blob> => {
  const res = await axios.get(
    `${BASE}/documents/${id}/download-partial/`,
    cfg(token, null, { responseType: 'blob' })
  );
  return res.data;
};

export const getCompanySignStats = async (
  token: string,
  signal: AbortSignal | null = null
): Promise<CompanySignStats> => {
  const res = await axios.get(`${BASE}/company/v2-stats/`, cfg(token, signal));
  return res.data;
};

// Crediti HR: stesso client e JWT delle API documenti.
export const getSignaturePackages = async (token: string, signal: AbortSignal | null = null) =>
  (await axios.get(`${BASE}/credits/packages/`, cfg(token, signal, { timeout: 20000 }))).data.results;

export const getSignatureBalance = async (token: string, signal: AbortSignal | null = null) =>
  (await axios.get(`${BASE}/credits/balance/`, cfg(token, signal, { timeout: 20000 }))).data;

export const createSignatureCheckout = async (token: string, payload: {
  package_id: number; quantity: number; idempotency_key: string;
  success_url: string; cancel_url: string;
}) => (await axios.post(`${BASE}/credits/checkout/`, payload,
  cfg(token, null, { timeout: 30000, headers: { Authorization: token, 'Content-Type': 'application/json' } }))).data;

export const getSignaturePurchase = async (token: string, id: number) =>
  (await axios.get(`${BASE}/credits/purchases/${id}/`, cfg(token, null, { timeout: 20000 }))).data;

export const signatureCreditError = (error: any): string => {
  const status = error?.response?.status;
  if (status === 402) return 'Per acquistare firme è necessario un abbonamento HR attivo.';
  if (status === 401 || status === 403) return 'Non sei autorizzato a gestire i crediti firma. Verifica il tuo accesso.';
  const message = error?.response?.data?.error || error?.response?.data?.detail;
  return typeof message === 'string' ? message : 'Operazione non riuscita. Verifica la connessione e riprova.';
};

// Tutti i calcoli contabili restano in centesimi interi; lordo unitario dal catalogo.
export const signatureQuote = (pkg: { gross_unit_cents: number; vat_rate: number }, quantity: number) => {
  const gross = pkg.gross_unit_cents * quantity;
  const divisor = 100 + pkg.vat_rate;
  const net = Math.floor((gross * 100 * 2 + divisor) / (divisor * 2));
  return { gross, net, vat: gross - net };
};
